import React from 'react';
import { Twitter, Linkedin, Instagram, Github, ArrowUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const footerLinks = {
    company: [
      { name: t.footer.links.aboutUs, href: '#about' },
      { name: t.footer.links.ourApps, href: '#apps' },
      { name: t.footer.links.services, href: '#services' },
      { name: t.footer.links.careers, href: '#' },
    ],
    support: [
      { name: t.footer.links.contact, href: '#contact' },
      { name: t.footer.links.faq, href: '#' },
      { name: t.footer.links.privacyPolicy, href: '#' },
      { name: t.footer.links.termsOfService, href: '#' },
    ],
  };

  const socialLinks = [
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Github, href: '#', label: 'GitHub' },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#302f2c] border-t border-[#1e3a5f]/50">
      <div className="max-w-[87.5rem] mx-auto px-10">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <a href="#home" className="flex items-center gap-2 mb-6">
              <span className="text-[#7dd3fc] font-black text-3xl tracking-tight">RDK</span>
              <span className="text-white font-medium text-xl">Services</span>
            </a>
            <p className="text-[#888680] text-base leading-relaxed mb-6 max-w-md">
              {t.footer.description}
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 bg-[#1a1c1b] border border-[#1e3a5f]/50 rounded-full flex items-center justify-center text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 transition-all duration-300"
                  >
                    <IconComponent size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-[#7dd3fc] font-bold text-sm uppercase tracking-wider mb-6">
              {t.footer.company}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-[#888680] hover:text-[#7dd3fc] transition-colors text-base"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-[#7dd3fc] font-bold text-sm uppercase tracking-wider mb-6">
              {t.footer.support}
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-[#888680] hover:text-[#7dd3fc] transition-colors text-base"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-[#1e3a5f]/50 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#888680] text-sm">
            {t.footer.copyright}
          </p>
          
          {/* Back to top */}
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-[#888680] hover:text-[#7dd3fc] transition-colors text-sm font-medium"
          >
            {t.footer.backToTop}
            <ArrowUp size={16} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
